import React from 'react'
import styled from 'styled-components'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { EmptyStateDescription } from '@lmc/ui-kit/src/dataDisplay/EmptyState'
import { colors } from '@lmc/common/constants'
import { ResidentItem } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import dayjs from 'dayjs'

type TabType = 'active' | 'discontinued'

type EMARScreenEmptyStateV2Props = {
  resident: ResidentItem
  type: TabType
  testId?: string
  discontinuedFromDate?: string | Date
}

const EMARScreenEmptyStateV2: React.FC<EMARScreenEmptyStateV2Props> = ({
  resident,
  type,
  testId,
  discontinuedFromDate
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const helpLink =
    'https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication'

  const residentDisplayName = resident.preferredName || resident.name.first

  const getTitle = () => {
    if (type === 'active') {
      return t('NO_ACTIVE_MEDICATIONS_TITLE', { name: residentDisplayName })
    }
    return t('NO_DISCONTINUED_MEDICATIONS_TITLE', { name: residentDisplayName })
  }

  const getDescription = () => {
    if (type === 'active') {
      return (
        <EmptyStateDescription>
          {t('NO_ACTIVE_MEDICATIONS_SUBTITLE')}{' '}
          <a href={helpLink} target="_blank" rel="noreferrer">
            {t('HERE')}
          </a>
          .
        </EmptyStateDescription>
      )
    }

    // For discontinued, format the date
    const formattedDate = discontinuedFromDate
      ? dayjs(discontinuedFromDate).format('DD-MM-YYYY')
      : dayjs().format('DD-MM-YYYY')

    return (
      <EmptyStateDescription>
        {t('NO_DISCONTINUED_MEDICATIONS_SUBTITLE', { date: formattedDate })}
      </EmptyStateDescription>
    )
  }

  return (
    <EmptyStateContainer
      data-testid={testId ? `${testId}-emar-empty-state` : 'emar-empty-state'}
    >
      <EmptyState
        icon={<NoDataIcon />}
        title={getTitle()}
        description={getDescription()}
        data-testid={
          testId
            ? `${testId}-emar-no-medications-empty-state`
            : 'emar-no-medications-empty-state'
        }
      />
    </EmptyStateContainer>
  )
}

const EmptyStateContainer = styled.div`
  width: 100%;
  margin-top: 20px;
  padding: 22px;
  background: ${colors.white};
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export default EMARScreenEmptyStateV2

